/**
 * The arithmetic an import performs on a Splitwise file's net effects, in one
 * place, so the preview and the server produce the same numbers.
 *
 * Two things have to hold for a group to open with the balances the file
 * shows:
 *
 * 1. Rounding each row's nets to its currency moves a member by up to a
 *    minor unit per row, and those deviations accumulate — a 42-row COP
 *    file already drifts by a peso with no exchange rate in sight. Stage A
 *    fixes the per-member totals to the rounded raw sums and folds the
 *    difference back into the last row of that currency.
 * 2. A currency that Splitwise shows as square must stay square once
 *    converted, so a settled currency converts at ONE rate (the one of its
 *    last row's date) and stage B folds the leftover cents the same way.
 *    Every converted row's payers and splits are then rebuilt from the
 *    converted nets, so the server stores exactly what the preview showed.
 */
import {
  getCurrencyDecimals,
  roundToCurrencyPrecision,
} from '@splitfiesta/shared';

import { convertAmount } from '../expense/ConversionService';
import { roundEffectsToCurrency } from './reconstructSplitwiseRow';

export interface ImportNetEffect {
  column: number;
  net: number;
}

export interface ImportConversionRow {
  rowIndex: number;
  date: string;
  currency: string;
  cost: number;
  kind: 'expense' | 'payment' | 'skipped';
  /**
   * Net per member column in the row's own currency, dense as the parser
   * writes it or sparse as the payload sends it: the same numbers in two
   * shapes, so the rule reads either without an adapter at each call site.
   */
  effects: number[] | ImportNetEffect[];
}

export interface ImportConversionDates {
  /** The date whose rate converts this row: its currency's settle date, or its own. */
  dateFor(row: { currency: string; date: string }): string;
  /** Foreign currencies whose rows still leave someone owing, first seen first. */
  openCurrencies: string[];
  /** Foreign currencies whose rows cancel out, first seen first. */
  settledCurrencies: string[];
}

export interface ImportReconciledRow {
  rowIndex: number;
  /**
   * The currency `nets` and `amount` are in: the group currency once the row
   * is converted, the row's own currency while no rate for it is known.
   */
  currency: string;
  /** Dense per-member nets, exact in `currency` and summing to zero. */
  nets: number[];
  /** The row's total in `currency`. */
  amount: number;
  /**
   * Set when the row moves no balance at all but still costs something: a
   * conversion can round every net to zero while the total rounds up to a
   * unit. The whole row is then charged to (and consumed by) this member, the
   * way a no-balance-effect row is, rather than being written as an expense
   * nobody paid.
   */
  soleColumn?: number;
}

export interface ImportReconciliation {
  /** The same lookup the rate fetches and the disclaimer read. */
  dates: ImportConversionDates;
  rows: ImportReconciledRow[];
  byRowIndex: Map<number, ImportReconciledRow>;
}

export const isUsableRate = (rate: number | undefined): rate is number =>
  typeof rate === 'number' && rate > 0 && Number.isFinite(rate);

export function netEffects(row: ImportConversionRow): ImportNetEffect[] {
  const effects = row.effects;
  if (effects.length === 0) return [];
  return typeof effects[0] === 'number'
    ? (effects as number[]).map((net, column) => ({ column, net }))
    : (effects as ImportNetEffect[]);
}

/**
 * Reads the rows that will import — never the file's `Total balance` lines,
 * which cover rows the reviewer may have left out — and decides, per foreign
 * currency, whether every member's net across it rounds to zero.
 */
export function conversionDatesFor(
  rows: ImportConversionRow[],
  groupCurrency: string
): ImportConversionDates {
  const group = groupCurrency.toUpperCase();
  const perCurrency = new Map<
    string,
    { latest: string; nets: Map<number, number> }
  >();

  for (const row of rows) {
    const currency = row.currency.toUpperCase();
    if (currency === group) continue;
    let entry = perCurrency.get(currency);
    if (!entry) {
      entry = { latest: row.date, nets: new Map() };
      perCurrency.set(currency, entry);
    }
    // `YYYY-MM-DD` sorts as text, so no Date is built for 2000 rows.
    if (row.date > entry.latest) entry.latest = row.date;
    for (const effect of netEffects(row)) {
      entry.nets.set(
        effect.column,
        (entry.nets.get(effect.column) ?? 0) + effect.net
      );
    }
  }

  const settleDates = new Map<string, string>();
  const openCurrencies: string[] = [];
  for (const [currency, entry] of perCurrency) {
    const settled = [...entry.nets.values()].every(
      net => roundToCurrencyPrecision(net, currency) === 0
    );
    if (settled) settleDates.set(currency, entry.latest);
    else openCurrencies.push(currency);
  }

  return {
    dateFor: row => settleDates.get(row.currency.toUpperCase()) ?? row.date,
    openCurrencies,
    settledCurrencies: [...settleDates.keys()],
  };
}

/**
 * Everything below counts in minor units (integers), so a fold of a few cents
 * over 2000 rows cannot pick up binary-floating-point dust of its own.
 */
const unitScale = (currency: string): number =>
  10 ** getCurrencyDecimals(currency);

const toUnits = (amount: number, currency: string): number =>
  Math.round(roundToCurrencyPrecision(amount, currency) * unitScale(currency));

const fromUnits = (units: number, currency: string): number =>
  roundToCurrencyPrecision(units / unitScale(currency), currency);

const sum = (values: number[]): number => values.reduce((s, v) => s + v, 0);

const positiveSum = (values: number[]): number =>
  values.reduce((s, v) => (v > 0 ? s + v : s), 0);

const indexOfMax = (values: number[]): number =>
  values.reduce((best, v, i) => (v > values[best] ? i : best), 0);

const indexOfMin = (values: number[]): number =>
  values.reduce((best, v, i) => (v < values[best] ? i : best), 0);

function columnCount(rows: ImportConversionRow[]): number {
  let columns = 0;
  for (const row of rows) {
    for (const effect of netEffects(row)) {
      columns = Math.max(columns, effect.column + 1);
    }
  }
  return columns;
}

function denseNets(row: ImportConversionRow, columns: number): number[] {
  const nets = new Array<number>(columns).fill(0);
  for (const effect of netEffects(row)) {
    if (effect.column >= 0 && effect.column < columns) {
      nets[effect.column] = effect.net;
    }
  }
  return nets;
}

/**
 * Moves `delta` (a zero-sum correction, in minor units) into the rows of one
 * currency, newest first, as member-to-member transfers.
 *
 * Only expense rows take it: a payment row has to keep its two-sided shape,
 * and the server charges a no-balance-effect row to the member the reviewer
 * named without reading its nets at all. A row absorbs at most
 * `amount − Σ positive nets`, which is what keeps every share ≥ 0 and
 * `Σ payers = Σ splits = amount` once `reconstructSplitwiseRow` rebuilds it;
 * whatever is left walks back to the previous row of the same currency.
 */
function absorb(
  delta: number[],
  rows: ImportConversionRow[],
  nets: number[][],
  amounts: number[]
): void {
  if (delta.every(d => d === 0)) return;

  const remaining = [...delta];
  for (let i = rows.length - 1; i >= 0; i -= 1) {
    if (rows[i].kind !== 'expense') continue;
    let capacity = amounts[i] - positiveSum(nets[i]);
    while (capacity > 0) {
      const taker = indexOfMax(remaining);
      const giver = indexOfMin(remaining);
      if (remaining[taker] <= 0 || remaining[giver] >= 0) return;
      const move = Math.min(capacity, remaining[taker], -remaining[giver]);
      nets[i][taker] += move;
      nets[i][giver] -= move;
      remaining[taker] -= move;
      remaining[giver] += move;
      capacity -= move;
    }
  }
}

/**
 * Rounding each member's total on its own can conjure (or lose) a unit, and a
 * group's balances have to sum to zero however the file rounds. The biggest
 * creditor gives up the odd unit — the same member `roundEffectsToCurrency`
 * charges it to inside a row — which is also what keeps the correction
 * `absorb` then spreads zero-sum: a file whose rows do not balance keeps its
 * own drift instead of having money invented for it.
 */
function conserve(target: number[], total: number): void {
  const mismatch = sum(target) - total;
  if (mismatch !== 0 && target.length > 0) {
    target[indexOfMax(target)] -= mismatch;
  }
}

/** Puts a row's nets back on `total` by moving the difference to its largest payer. */
function settleRow(nets: number[], total: number): void {
  const drift = total - sum(nets);
  if (drift !== 0 && nets.length > 0) nets[indexOfMax(nets)] += drift;
}

/**
 * Converting each net on its own can leave the payers owed more than the row
 * costs (each conversion rounds outward by up to half a unit), which
 * `reconstructSplitwiseRow` could only answer with a negative share. Moving
 * the excess from the largest payer to the deepest debtor costs both sides
 * the same and keeps the row zero-sum.
 */
function clampToAmount(nets: number[], amount: number): void {
  let excess = positiveSum(nets) - amount;
  while (excess > 0) {
    const from = indexOfMax(nets);
    const to = indexOfMin(nets);
    if (nets[from] <= 0 || nets[to] >= 0) return;
    const move = Math.min(excess, nets[from], -nets[to]);
    nets[from] -= move;
    nets[to] += move;
    excess -= move;
  }
}

/**
 * The nets each row will actually be stored with, per member column.
 *
 * Without `rateFor` this is stage A only — every currency, including the
 * group's, in its own currency — which is what the payload carries. With
 * `rateFor` each foreign currency whose rates are all known is also
 * converted (stage B) and comes back in the group currency; a currency
 * missing a rate stays in its own, which is how `balancesAfterImport` knows
 * it cannot show a total yet and how the server knows that row must fail.
 *
 * Idempotent: run again on rows built from its own output and nothing moves.
 */
export function reconciledImportNets(
  rows: ImportConversionRow[],
  groupCurrency: string,
  rateFor?: (currency: string, date: string) => number | undefined
): ImportReconciliation {
  const group = groupCurrency.toUpperCase();
  const dates = conversionDatesFor(rows, groupCurrency);
  const columns = columnCount(rows);

  const byCurrency = new Map<string, ImportConversionRow[]>();
  for (const row of rows) {
    const currency = row.currency.toUpperCase();
    const list = byCurrency.get(currency);
    if (list) list.push(row);
    else byCurrency.set(currency, [row]);
  }

  const reconciled: ImportReconciledRow[] = [];
  const byRowIndex = new Map<number, ImportReconciledRow>();

  for (const [currency, currencyRows] of byCurrency) {
    const nets = currencyRows.map(row => {
      const rounded = roundEffectsToCurrency(
        denseNets(row, columns),
        currency
      ).map(net => toUnits(net, currency));
      if (row.kind === 'payment') {
        // The parser calls a row a payment on one positive and one negative
        // net, not on the two mirroring each other. A settlement moves ONE
        // amount, so the two sides are made to agree here and any difference
        // goes to the currency's residual — otherwise the preview would add
        // up one figure and the settlement would move the other.
        const from = rounded.findIndex(net => net > 0);
        const to = rounded.findIndex(net => net < 0);
        if (from >= 0 && to >= 0) rounded[to] = -rounded[from];
      }
      return rounded;
    });
    const amounts = currencyRows.map(row => toUnits(row.cost, currency));
    // Who fronted each row, read before any conversion can flatten its nets.
    const payerColumns = nets.map(row => {
      const column = indexOfMax(row);
      return row[column] > 0 ? column : undefined;
    });

    // Stage A: the per-member totals the file says this currency holds.
    const target = new Array<number>(columns).fill(0);
    for (const row of currencyRows) {
      denseNets(row, columns).forEach((net, column) => {
        target[column] += net;
      });
    }
    const targetUnits = target.map(total => toUnits(total, currency));
    const actual = new Array<number>(columns).fill(0);
    for (const row of nets)
      row.forEach((net, column) => (actual[column] += net));
    conserve(targetUnits, sum(actual));
    absorb(
      targetUnits.map((t, column) => t - actual[column]),
      currencyRows,
      nets,
      amounts
    );

    const stageA = () => {
      currencyRows.forEach((row, i) => {
        const entry: ImportReconciledRow = {
          rowIndex: row.rowIndex,
          currency,
          nets: nets[i].map(net => fromUnits(net, currency)),
          amount: fromUnits(amounts[i], currency),
        };
        reconciled.push(entry);
        byRowIndex.set(row.rowIndex, entry);
      });
    };

    if (!rateFor || currency === group) {
      stageA();
      continue;
    }

    const rates = currencyRows.map(row =>
      rateFor(currency, dates.dateFor(row))
    );
    if (!rates.every(isUsableRate)) {
      stageA();
      continue;
    }

    // Stage B: the same nets in the group currency.
    const groupNets: number[][] = [];
    const groupAmounts: number[] = [];
    currencyRows.forEach((row, i) => {
      const rate = rates[i] as number;
      const convert = (units: number): number =>
        toUnits(convertAmount(fromUnits(units, currency), rate, group), group);
      groupAmounts[i] = convert(amounts[i]);
      if (row.kind === 'payment') {
        // A payment moves one amount between two members; converting the two
        // nets separately could leave them a cent apart and stop being one.
        const from = nets[i].findIndex(net => net > 0);
        const to = nets[i].findIndex(net => net < 0);
        const moved = from >= 0 ? convert(nets[i][from]) : 0;
        groupNets[i] = new Array<number>(columns).fill(0);
        if (from >= 0) groupNets[i][from] = moved;
        if (to >= 0) groupNets[i][to] = -moved;
        return;
      }
      groupNets[i] = nets[i].map(convert);
      settleRow(groupNets[i], convert(sum(nets[i])));
      clampToAmount(groupNets[i], groupAmounts[i]);
    });

    if (dates.settledCurrencies.includes(currency)) {
      // Every row of a settled currency shares one rate, so what this
      // currency is worth in the group currency is that rate applied to its
      // (zero, by stage A) per-member totals.
      const rate = rates[0] as number;
      const converted = new Array<number>(columns).fill(0);
      for (const row of groupNets) {
        row.forEach((net, column) => (converted[column] += net));
      }
      // Read back from the rows: stage A normally lands them on zero, but a
      // currency it could not fully correct must not be "corrected" here into
      // a total it never had.
      const settled = new Array<number>(columns).fill(0);
      for (const row of nets)
        row.forEach((net, column) => (settled[column] += net));
      const settledTarget = settled.map(total =>
        toUnits(convertAmount(fromUnits(total, currency), rate, group), group)
      );
      conserve(settledTarget, sum(converted));
      absorb(
        settledTarget.map((t, column) => t - converted[column]),
        currencyRows,
        groupNets,
        groupAmounts
      );
    }

    currencyRows.forEach((row, i) => {
      // A row worth less than a minor unit per member converts to nothing but
      // still costs one: `reconstructSplitwiseRow` would answer with no payers
      // and no splits, so the member who fronted it takes the whole thing.
      const collapsed =
        groupAmounts[i] > 0 && groupNets[i].every(net => net === 0);
      const entry: ImportReconciledRow = {
        rowIndex: row.rowIndex,
        currency: group,
        nets: groupNets[i].map(net => fromUnits(net, group)),
        amount: fromUnits(groupAmounts[i], group),
        ...(collapsed && payerColumns[i] !== undefined
          ? { soleColumn: payerColumns[i] }
          : {}),
      };
      reconciled.push(entry);
      byRowIndex.set(row.rowIndex, entry);
    });
  }

  return { dates, rows: reconciled, byRowIndex };
}
